import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { useUpsertTier } from '../../hooks';
import type { LoyaltyTier } from '../../model/types';

interface TierFormDialogProps {
  open: boolean;
  /** The tier being edited, or `null` to create a new one. */
  tier: LoyaltyTier | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

/** Create / update a loyalty tier (upsert by code; gated by `loyalty.manage`). */
export function TierFormDialog({ open, tier, onClose, onDone }: TierFormDialogProps) {
  const editing = tier !== null;
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [minPoints, setMinPoints] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [benefits, setBenefits] = useState('');
  const [error, setError] = useState<string | null>(null);
  const upsert = useUpsertTier();

  useEffect(() => {
    if (open) {
      setCode(tier?.code ?? '');
      setName(tier?.name ?? '');
      setMinPoints(tier ? String(tier.minPoints) : '');
      setSortOrder(tier?.sortOrder != null ? String(tier.sortOrder) : '');
      setBenefits(tier?.benefits ?? '');
      setError(null);
    }
  }, [open, tier]);

  const minPointsNum = Number(minPoints);
  const sortOrderNum = sortOrder.trim() ? Number(sortOrder) : undefined;
  const validMin = minPoints.trim().length > 0 && Number.isInteger(minPointsNum) && minPointsNum >= 0;
  const validSort = sortOrderNum === undefined || Number.isInteger(sortOrderNum);
  const valid = code.trim().length > 0 && name.trim().length > 0 && validMin && validSort;

  const submit = () => {
    setError(null);
    upsert.mutate(
      {
        code: code.trim(),
        name: name.trim(),
        minPoints: minPointsNum,
        sortOrder: sortOrderNum,
        benefits: benefits.trim() || undefined,
      },
      {
        onSuccess: (saved) => {
          onDone(`Уровень «${saved.name}» сохранён.`);
          onClose();
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? 'Изменить уровень' : 'Новый уровень'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Код"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            fullWidth
            disabled={editing}
            autoFocus={!editing}
            slotProps={{ htmlInput: { maxLength: 64 } }}
            helperText={editing ? 'Код нельзя изменить (обновление по коду).' : 'Напр. GOLD'}
          />
          <TextField
            label="Название"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <TextField
            label="Минимум баллов"
            type="number"
            value={minPoints}
            onChange={(e) => setMinPoints(e.target.value)}
            error={minPoints.trim().length > 0 && !validMin}
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
            helperText="Порог накопленных баллов для уровня"
          />
          <TextField
            label="Порядок"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            error={sortOrder.trim().length > 0 && !validSort}
            fullWidth
            slotProps={{ htmlInput: { step: 1 } }}
            helperText="Необязательно — порядок отображения в лестнице уровней"
          />
          <TextField
            label="Преимущества"
            value={benefits}
            onChange={(e) => setBenefits(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={upsert.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={upsert.isPending || !valid}
          startIcon={upsert.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
